import { getBaySlots, getIsoBaysUnderLid, } from "@tedivo/tedivo-bay-grid-pure";
import { GRID_OPTIONS_BY_CELL_DIMENSION, GRID_OPTIONS_BY_CELL_GAP } from "./gridConfigConstants";
import { calculateGridFromConfigDimensions } from "@tedivo/tedivo-bay-grid-core";
import CellSpacingEnum from "../../../../../app/enums/CellSpacingEnum";
import GroupBaysPairedEnum from "../../../../../app/enums/GroupBaysPairedEnum";
import SizeSmallMidLargeEnum from "../../../../../app/enums/SizeSmallMidLargeEnum";
import { createFnSlotCell } from "./createFnSlotCell";
import { createLegend } from "./createLegend";
import { drawBayStackWeights } from "./drawBayStackWeights";
import { getGridNodesOptions } from "./getGridNodesOptions";
import { getTranslation } from "../../../../../app/i18/i18tn";
import globalStore from "../../../../../app/stores/globalStore";
import ovdJsonStore from "../../../../../app/stores/OVDJsonStore";
import { removeChildren } from "@tedivo/tedivo-dom-helpers";
import { roundDec } from "@tedivo/tedivo-pure-helpers";
import { setPreferencesKeyAndValue } from "@tedivo/tedivo-preferences";
/** Bay boxes drawn per frame in a partial update */
const BAYS_PER_FRAME = 6;
/** Order of the size buttons, smallest first */
const CELL_SIZES_ORDER = [
    SizeSmallMidLargeEnum.XSMALL,
    SizeSmallMidLargeEnum.SMALL,
    SizeSmallMidLargeEnum.MIDDLE,
    SizeSmallMidLargeEnum.LARGE,
    SizeSmallMidLargeEnum.XLARGE,
];
/** Sizes small enough to scale to the screen and fit 2 bays vertically */
const isSmallCellSize = (size) => size === SizeSmallMidLargeEnum.SMALL || size === SizeSmallMidLargeEnum.XSMALL;
class BaysSection {
    constructor(props) {
        this.bayBoxesComponents = new Map();
        this.blocksOfBaysNodes = new Map();
        this.bayDrawOptions = {
            cellSize: SizeSmallMidLargeEnum.SMALL,
            cellSpacing: CellSpacingEnum.NO_SPACE,
            baysPairedGrouped: GroupBaysPairedEnum.NOT_GROUPED,
            fit2Vertical: false,
            showCGs: false,
        };
        this._scale = 1;
        /** Frame drawing the next batch of a partial update, and the Bays still waiting for it */
        this.pendingFrame = undefined;
        this.pendingBays = [];
        this.props = props;
        const { mainNode, legendNode, baysNode, helpersNode, sizeRadioGroup, spacedSlotsSwitch, groupedByBaySwitch, fit2VerticalSwitch, showCGsSwitch, } = buildHtml({
            bayDrawOptions: this.bayDrawOptions,
            shipName: this.props.shipName,
        });
        this.mainNode = mainNode;
        this.baysNode = baysNode;
        this.legendNode = legendNode;
        this.helpersNode = helpersNode;
        this.sizeRadioGroup = sizeRadioGroup;
        this.spacedSlotsSwitch = spacedSlotsSwitch;
        this.groupedByBaySwitch = groupedByBaySwitch;
        this.fit2VerticalSwitch = fit2VerticalSwitch;
        this.showCGsSwitch = showCGsSwitch;
        this.nodesOptions = getGridNodesOptions();
    }
    calcAndSetScale(scaleActive) {
        let availableHeight = 0;
        let realScale = 1;
        const json = ovdJsonStore.currentJson;
        const isSmallSize = isSmallCellSize(this.bayDrawOptions.cellSize);
        if (scaleActive && isSmallSize && (json === null || json === void 0 ? void 0 : json.sizeSummary.maxRow) !== undefined) {
            let baysTitlePartHeight = 60;
            const baysTitlePart = document.getElementById("bays-title-part");
            if (baysTitlePart)
                baysTitlePartHeight = baysTitlePart.clientHeight;
            availableHeight = window.innerHeight - baysTitlePartHeight;
            const { height } = calculateGridFromConfigDimensions(Object.assign(Object.assign(Object.assign(Object.assign({}, json.sizeSummary), GRID_OPTIONS_BY_CELL_DIMENSION[this.bayDrawOptions.cellSize]), GRID_OPTIONS_BY_CELL_GAP[this.bayDrawOptions.cellSpacing]), { maxRow: json.sizeSummary.maxRow }));
            realScale = roundDec(availableHeight / (2 * (60 + height + 30)), 5);
        }
        if (realScale > 1)
            realScale = 1;
        this.bayBoxesComponents.forEach((comp) => {
            comp.scale = realScale;
        });
        this._scale = realScale;
    }
    render({ bayDrawOptions, baysToRender, lcgVcgTcgAndPairings, }) {
        const json = ovdJsonStore.currentJson;
        if (!json || !lcgVcgTcgAndPairings) {
            return;
        }
        // CGs (Bottom Bases + TCGs) can only be shown when the ship has at least one of
        // them; downgrade (and disable the switch) when both are missing, regardless of
        // the stored preference.
        const cgsUnavailable = lcgVcgTcgAndPairings.missingImportantVcgs && lcgVcgTcgAndPairings.missingImportantTcgs;
        this.bayDrawOptions = Object.assign(Object.assign({}, bayDrawOptions), { showCGs: bayDrawOptions.showCGs && !cgsUnavailable });
        const shipData = json.shipData;
        this.nodesOptions = getGridNodesOptions();
        if (!baysToRender) {
            // Legend
            removeChildren(this.legendNode);
            this.legendNode.appendChild(createLegend(shipData.containersLengths, shipData.featuresAllowed));
            // Helpers
            removeChildren(this.helpersNode);
            if (this.props.bayHelpersButton)
                this.helpersNode.appendChild(this.props.bayHelpersButton);
        }
        this.sizeRadioGroup.value = String(this.bayDrawOptions.cellSize);
        this.spacedSlotsSwitch.checked = this.bayDrawOptions.cellSpacing === CellSpacingEnum.SPACED;
        this.groupedByBaySwitch.checked =
            this.bayDrawOptions.baysPairedGrouped === GroupBaysPairedEnum.GROUPED;
        this.fit2VerticalSwitch.checked = this.bayDrawOptions.fit2Vertical;
        this.fit2VerticalSwitch.disabled = !isSmallCellSize(this.bayDrawOptions.cellSize);
        this.showCGsSwitch.disabled = cgsUnavailable;
        this.showCGsSwitch.checked = this.bayDrawOptions.showCGs;
        // Bays
        this.renderBays(json, this.bayDrawOptions, baysToRender, lcgVcgTcgAndPairings);
    }
    renderBays(json, bayDrawOptions, baysToDraw, lcgVcgTcgAndPairings) {
        const { shipData, baysData: bls, sizeSummary, lidData } = json;
        // A newer render takes over the Bays an older partial one had not reached yet.
        // A full render redraws them all anyway
        if (this.pendingFrame !== undefined)
            cancelAnimationFrame(this.pendingFrame);
        this.pendingFrame = undefined;
        const leftovers = this.pendingBays;
        this.pendingBays = [];
        if (baysToDraw !== undefined)
            baysToDraw = [...baysToDraw, ...leftovers];
        const maxRow = sizeSummary.maxRow;
        if (maxRow === undefined || maxRow === null) {
            return document.createElement("div");
        }
        const { cellSize: size, cellSpacing: cellpadding } = bayDrawOptions;
        this.baysNode.className = `bays-wrapper cSize-${SizeSmallMidLargeEnum[size]} cSep-${CellSpacingEnum[cellpadding]}`;
        // Create divs for each block of bays
        const { blockBaysAndSizes, blockBaysAndSizesBy20Bay } = lcgVcgTcgAndPairings;
        blockBaysAndSizes.forEach((b, idx) => {
            const blockNode = this.blocksOfBaysNodes.get(b.allBays);
            const newOrder = String(idx);
            if (blockNode) {
                const order = blockNode.dataset.order;
                if (newOrder !== order) {
                    blockNode.style.order = newOrder;
                }
            }
            else {
                const newBlockNode = document.createElement("div");
                newBlockNode.className = "block-bays";
                newBlockNode.id = `block-bays-${b.allBays}`;
                newBlockNode.style.order = newOrder;
                this.blocksOfBaysNodes.set(b.allBays, newBlockNode);
                this.baysNode.appendChild(newBlockNode);
            }
        });
        // Grid dimensions
        const { width, height } = calculateGridFromConfigDimensions(Object.assign(Object.assign(Object.assign(Object.assign({}, sizeSummary), GRID_OPTIONS_BY_CELL_DIMENSION[size]), GRID_OPTIONS_BY_CELL_GAP[cellpadding]), { maxRow }));
        // Validation
        const validationResult = ovdJsonStore.ovdValidator.getLastResult();
        // Calculate the lids of each bay
        const lidsOfBay = createLidsDictByBay(lidData, validationResult);
        const uniqueBays = getUniqueBays(bls);
        const symbolsCache = new Map();
        const createFnSlotCellToUse = (d) => createFnSlotCell(d, globalStore.resolvedColorMode === "dark", "", symbolsCache);
        const drawBay = (isoBay) => {
            let bayBoxComp = this.bayBoxesComponents.get(isoBay);
            if (!bayBoxComp) {
                // Create new bay box component
                bayBoxComp = document.createElement("bay-box-component");
                bayBoxComp.setAttribute("id", `bay-box-component-${isoBay}`);
                bayBoxComp.style.order = isoBay;
                // Initialize the component
                bayBoxComp.setDrawingColors(this.nodesOptions).setDrawingProps({
                    createFnSlotCell: createFnSlotCellToUse,
                    createEditBayButtonFunction: this.props.createEditBayButtonFunction,
                    drawStackWeights: drawBayStackWeights,
                });
                // Save the component for reuse in next render
                this.bayBoxesComponents.set(isoBay, bayBoxComp);
            }
            bayBoxComp
                .setDrawingColors(this.nodesOptions)
                .setCreateFnSlotCell(createFnSlotCellToUse)
                .setSize(width, height)
                .setData(Object.assign({ sizeSummary,
                shipData, lidData: lidsOfBay[isoBay] || [], isoBay,
                width,
                height, ssMaxRow: maxRow, cellSize: size, cellSeparation: cellpadding, validationResult, showBottomBases: bayDrawOptions.showCGs, showTCGs: bayDrawOptions.showCGs }, getBaySlots(isoBay, bls)));
        };
        // Appends the component to its block of bays node
        const placeBay = (isoBay) => {
            var _a;
            const bayBoxComp = this.bayBoxesComponents.get(isoBay);
            if (!bayBoxComp)
                return;
            const blockOfBaysNodeName = (_a = blockBaysAndSizesBy20Bay[isoBay]) === null || _a === void 0 ? void 0 : _a.allBays;
            if (!blockOfBaysNodeName)
                return;
            const blockOfBaysNode = this.blocksOfBaysNodes.get(blockOfBaysNodeName);
            if (!blockOfBaysNode)
                return;
            if (bayBoxComp.parentNode !== blockOfBaysNode) {
                blockOfBaysNode.appendChild(bayBoxComp);
            }
            bayBoxComp.scale = this._scale;
        };
        const toDraw = uniqueBays.filter((isoBay) => !baysToDraw || baysToDraw.includes(isoBay));
        // A partial update (e.g. many Bays saved from Bay Edit) is spread over frames,
        // so the page stays responsive. A full one stays synchronous, as on first load
        const firstBatch = baysToDraw === undefined ? toDraw : toDraw.slice(0, BAYS_PER_FRAME);
        firstBatch.forEach(drawBay);
        // Commands over each BayBoxComponent
        uniqueBays.forEach(placeBay);
        this.pendingBays = toDraw.slice(firstBatch.length);
        const drawNextBatch = () => {
            this.pendingFrame = undefined;
            const batch = this.pendingBays.splice(0, BAYS_PER_FRAME);
            batch.forEach((isoBay) => {
                drawBay(isoBay);
                placeBay(isoBay);
            });
            if (this.pendingBays.length)
                this.pendingFrame = requestAnimationFrame(drawNextBatch);
        };
        if (this.pendingBays.length)
            this.pendingFrame = requestAnimationFrame(drawNextBatch);
    }
}
export default BaysSection;
function buildHtml({ bayDrawOptions, shipName, }) {
    const mainNode = document.createElement("div");
    mainNode.className = `oss-card holder-bays`;
    const baysNode = document.createElement("div");
    const legendNode = document.createElement("div");
    const helpersNode = document.createElement("div");
    helpersNode.className = "helpers-holder helpers-bays";
    const titlePart = document.createElement("title-with-actions-component");
    titlePart.titleHtml = `${getTranslation("view:bayDataTitle")} &middot; ${shipName}`;
    titlePart.id = "bays-title-part";
    titlePart.titleNodeName = "h2";
    const { cellSize, cellSpacing, baysPairedGrouped, fit2Vertical, showCGs } = bayDrawOptions;
    // Actions
    const groupedByBaySwitch = document.createElement("sl-switch");
    groupedByBaySwitch.innerHTML = getTranslation("view:baysGrouped");
    groupedByBaySwitch.size = "small";
    groupedByBaySwitch.className = "margin-left-05";
    groupedByBaySwitch.addEventListener("sl-change", (e) => {
        const newBayGrouped = e.target.checked
            ? GroupBaysPairedEnum.GROUPED
            : GroupBaysPairedEnum.NOT_GROUPED;
        setPreferencesKeyAndValue("bay-grouped-paired", newBayGrouped);
        document.documentElement.dispatchEvent(new CustomEvent("bayDisplayPairedGroupChanged", {
            detail: {
                newBayGrouped,
                newBaySize: undefined,
                newCellSeparation: undefined,
                fit2Vertical: undefined,
            },
        }));
    });
    const fit2VerticalSwitch = document.createElement("sl-switch");
    fit2VerticalSwitch.innerHTML = getTranslation("view:fit2Vertical");
    fit2VerticalSwitch.size = "small";
    fit2VerticalSwitch.className = "margin-left-05";
    fit2VerticalSwitch.addEventListener("sl-change", (e) => {
        const fit2Vertical = e.target.checked;
        setPreferencesKeyAndValue("bay-fit2-vertical", fit2Vertical);
        document.documentElement.dispatchEvent(new CustomEvent("bayDisplayFit2VerticalChanged", {
            detail: {
                newBayGrouped: undefined,
                newBaySize: undefined,
                newCellSeparation: undefined,
                fit2Vertical,
            },
        }));
    });
    const showCGsSwitch = document.createElement("sl-switch");
    showCGsSwitch.innerHTML = getTranslation("view:showCGs");
    showCGsSwitch.size = "small";
    showCGsSwitch.className = "margin-left-05";
    showCGsSwitch.addEventListener("sl-change", (e) => {
        const newShowCGs = e.target.checked;
        setPreferencesKeyAndValue("bay-show-cgs", newShowCGs);
        document.documentElement.dispatchEvent(new CustomEvent("bayDisplaySizeChanged", {
            detail: {
                newBaySize: undefined,
                newCellSeparation: undefined,
                newBayGrouped: undefined,
                fit2Vertical: undefined,
                showCGs: newShowCGs,
            },
        }));
    });
    const sizeRadioGroup = document.createElement("sl-radio-group");
    sizeRadioGroup.size = "small";
    sizeRadioGroup.className = "bay-size-radio";
    sizeRadioGroup.title = getTranslation("view:bayDataSize");
    CELL_SIZES_ORDER.forEach((size) => {
        const name = SizeSmallMidLargeEnum[size];
        const button = document.createElement("sl-radio-button");
        button.value = String(size);
        button.title = getTranslation(`enums:SizeSmallMidLargeEnum.${name}`);
        button.innerHTML = getTranslation(`enums:SizeSmallMidLargeEnumShort.${name}`);
        sizeRadioGroup.appendChild(button);
    });
    sizeRadioGroup.addEventListener("sl-change", () => {
        const newBaySize = Number(sizeRadioGroup.value);
        let fit2Vertical = undefined;
        if (isSmallCellSize(newBaySize)) {
            fit2Vertical = fit2VerticalSwitch.checked;
            fit2VerticalSwitch.disabled = false;
        }
        else {
            fit2Vertical = false;
            fit2VerticalSwitch.disabled = true;
        }
        setPreferencesKeyAndValue("bay-data-size", newBaySize);
        document.documentElement.dispatchEvent(new CustomEvent("bayDisplaySizeChanged", {
            detail: {
                newBaySize,
                newCellSeparation: undefined,
                newBayGrouped: undefined,
                fit2Vertical,
            },
        }));
    });
    const spacedSlotsSwitch = document.createElement("sl-switch");
    spacedSlotsSwitch.innerHTML = getTranslation("view:bayCellSeparation");
    spacedSlotsSwitch.size = "small";
    spacedSlotsSwitch.className = "margin-left-05";
    spacedSlotsSwitch.addEventListener("sl-change", (e) => {
        const newCellSeparation = e.target.checked
            ? CellSpacingEnum.SPACED
            : CellSpacingEnum.NO_SPACE;
        setPreferencesKeyAndValue("bay-cell-separation", newCellSeparation);
        document.documentElement.dispatchEvent(new CustomEvent("bayDisplaySizeChanged", {
            detail: {
                newBaySize: undefined,
                newCellSeparation,
                newBayGrouped: undefined,
                fit2Vertical: undefined,
            },
        }));
    });
    sizeRadioGroup.value = String(cellSize);
    spacedSlotsSwitch.checked = cellSpacing === CellSpacingEnum.SPACED;
    fit2VerticalSwitch.checked = fit2Vertical;
    fit2VerticalSwitch.disabled = !isSmallCellSize(cellSize);
    groupedByBaySwitch.value = baysPairedGrouped === GroupBaysPairedEnum.GROUPED ? "true" : "false";
    showCGsSwitch.checked = showCGs;
    const bayViewOptionsDiv = document.createElement("div");
    bayViewOptionsDiv.className = "bayView-options";
    bayViewOptionsDiv.appendChild(fit2VerticalSwitch);
    bayViewOptionsDiv.appendChild(groupedByBaySwitch);
    bayViewOptionsDiv.appendChild(showCGsSwitch);
    bayViewOptionsDiv.appendChild(spacedSlotsSwitch);
    bayViewOptionsDiv.appendChild(sizeRadioGroup);
    const actionsDiv = document.createElement("div");
    actionsDiv.appendChild(helpersNode);
    actionsDiv.appendChild(bayViewOptionsDiv);
    actionsDiv.slot = "actions";
    titlePart.appendChild(actionsDiv);
    mainNode.appendChild(titlePart);
    mainNode.appendChild(legendNode);
    mainNode.appendChild(baysNode);
    return {
        mainNode,
        baysNode,
        legendNode,
        helpersNode,
        sizeRadioGroup,
        spacedSlotsSwitch,
        groupedByBaySwitch,
        fit2VerticalSwitch,
        showCGsSwitch,
    };
}
function getUniqueBays(bls) {
    return bls.map((bl) => bl.isoBay).filter((v, idx, arr) => arr.indexOf(v) === idx);
}
function createLidsDictByBay(lidData, validationResult) {
    // Create a hash for each lid data
    const createLidHash = (v) => ({
        hash: `${v.label}|${v.startIsoBay}|${v.endIsoBay}|${v.starboardIsoRow}|${v.portIsoRow}|${v.overlapPort || ""}|${v.overlapStarboard || ""}|${v.weight || 0}`,
        notValid: v.notValid || undefined,
    });
    // Get the lids validation result
    const lidsValidationResult = validationResult.find((v) => v.name === "lidDataForBaysValidator");
    const lidsValidationResultData = lidsValidationResult === null || lidsValidationResult === void 0 ? void 0 : lidsValidationResult.result;
    const invalidResultsHash = new Map(lidsValidationResultData === null || lidsValidationResultData === void 0 ? void 0 : lidsValidationResultData.invalidResults.map((v) => {
        const { hash, notValid } = createLidHash(v);
        return [hash, notValid];
    }));
    return lidData.reduce((acc, v) => {
        const { hash } = createLidHash(v);
        const notValid = invalidResultsHash.get(hash);
        // Every bay the lid lies over, not only its start and end bays
        getIsoBaysUnderLid(v).forEach((isoBay) => {
            if (!acc[isoBay])
                acc[isoBay] = [];
            acc[isoBay].push(Object.assign(Object.assign({}, v), { notValid }));
        });
        return acc;
    }, {});
}
